import { ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { Typography } from "@platform/design-system";

import NoDataMessage from "../no-data-message";
import { Policy } from "../../constants/aquafina";
import { NOT_PROVIDED } from "../table/cells";

import CollapsableSection from "./collapsable-section";

const SECTION_BORDER_COLOR = "neutrals.neutral_200";

type Props = Pick<
  Policy,
  "groupInformation" | "groupContacts" | "billing" | "benefitClasses"
> & {
  colSpan: number;
};

const GroupInfo = ({
  groupInformation,
  groupContacts,
  billing,
  benefitClasses,
  colSpan,
}: Props): React.JSX.Element => (
  <CollapsableSection
    numberOfColumns={colSpan}
    title="Group Information"
    hasSeparator={false}
  >
    <StyledRow bgColor="white">
      <TableCell
        sx={{
          borderLeft: "1px solid",
          borderRight: "1px solid",
          borderColor: SECTION_BORDER_COLOR,
          fontSize: "16px",
        }}
        colSpan={colSpan}
      >
        <Box display="flex" columnGap={1}>
          <Detail title="Group Name">{groupInformation.groupName}</Detail>
          <Detail title="Group Number">{groupInformation.groupNumber}</Detail>
          <Detail title="Group Address">{groupInformation.groupAddress}</Detail>
          <Detail title="FEIN">{groupInformation.fein}</Detail>
        </Box>
      </TableCell>
    </StyledRow>
    <TableRowSection colSpan={colSpan}>Group Contacts</TableRowSection>
    <StyledRow bgColor="white">
      <TableCell
        colSpan={colSpan}
        sx={{
          px: 0,
          borderLeft: "1px solid",
          borderRight: "1px solid",
          borderColor: SECTION_BORDER_COLOR,
        }}
      >
        {groupContacts.length > 0 ? (
          <Box display="flex" columnGap={1} px={2}>
            {groupContacts.map((contact) => (
              <Box
                key={`${contact.name}-${contact.workEmail}`}
                display="flex"
                flexDirection="column"
                flex={1}
              >
                <Typography>
                  <em>{contact.name}</em>
                  {contact.isPrimary ? (
                    <Typography
                      component="span"
                      sx={{
                        backgroundColor: "surfaces.neutral",
                        px: 1.25,
                        py: 0.375,
                        borderRadius: 4,
                        ml: 1,
                      }}
                    >
                      Primary
                    </Typography>
                  ) : null}
                </Typography>
                <Box>
                  <Typography>{contact.workEmail}</Typography>
                  <Typography>{contact.workPhone}</Typography>
                  <Typography>{contact.role}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <NoDataMessage />
        )}
      </TableCell>
    </StyledRow>
    <TableSection
      colSpan={colSpan}
      title="Billing Divisions"
      headers={[
        "Billing Group Identifier",
        "Billing Group Name",
        "Description",
      ]}
      data={billing.map((row) => [row.identifier, row.name, row.description])}
    />
    <TableSection
      colSpan={colSpan}
      title="Benefit Eligibility Classes"
      headers={["Class Identifier", "Class Name", "Min. Weekly Hours Worked"]}
      data={benefitClasses.map((row) => [
        row.identifier,
        row.name,
        row.minWeeklyEligibleHours !== null &&
        row.minWeeklyEligibleHours !== undefined
          ? `${row.minWeeklyEligibleHours} hours`
          : "-",
      ])}
    />
  </CollapsableSection>
);

function Detail({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box flex={1} display="flex" flexDirection="column" rowGap={1}>
      <Typography>
        <strong>{title}</strong>
      </Typography>
      <Typography whiteSpace="pre-line">{children || NOT_PROVIDED}</Typography>
    </Box>
  );
}

function TableRowSection({
  children,
  colSpan,
}: {
  children: ReactNode;
  colSpan: number;
}) {
  return (
    <StyledRow bgColor="neutrals.neutral_200">
      <TableCell colSpan={colSpan} sx={{ py: 1 }}>
        <Typography>
          <strong>{children}</strong>
        </Typography>
      </TableCell>
    </StyledRow>
  );
}

function TableSection({
  title,
  colSpan,
  headers,
  data,
}: {
  title: string;
  colSpan: number;
  headers: string[];
  data: string[][];
}) {
  return (
    <>
      <TableRowSection colSpan={colSpan}>{title}</TableRowSection>
      <StyledRow bgColor="white">
        <TableCell
          colSpan={colSpan}
          sx={{
            px: 0,
            borderLeft: "1px solid",
            borderRight: "1px solid",
            borderColor: SECTION_BORDER_COLOR,
          }}
        >
          {data.length > 0 ? (
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  {headers.map((header, i) => (
                    <TableCell
                      key={header}
                      align={i === 0 ? "left" : "center"}
                      sx={[
                        {
                          fontSize: "16px",
                          border: "none",
                        },
                        i !== 0
                          ? {
                              borderLeft: "1px solid",
                              borderLeftColor: "neutrals.neutral_300",
                            }
                          : null,
                      ]}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, i) => (
                  <StyledRow
                    bgColor={i % 2 === 0 ? "neutrals.neutral_50" : "none"}
                    key={`${row[0]}-${row[1]}`}
                  >
                    {row.map((cell, j) =>
                      j === 0 ? (
                        <TableCell
                          key={headers[j]}
                          sx={{
                            fontSize: "16px",
                            border: "none",
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            columnGap={0.5}
                            paddingLeft={3}
                          >
                            <SubdirectoryArrowRightIcon fontSize="small" />
                            {cell}
                          </Box>
                        </TableCell>
                      ) : (
                        <TableCell
                          key={headers[j]}
                          align="center"
                          sx={{
                            fontSize: "16px",
                            border: "none",
                            borderLeft: "1px solid",
                            borderLeftColor: "neutrals.neutral_300",
                          }}
                        >
                          {cell || "-"}
                        </TableCell>
                      ),
                    )}
                  </StyledRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <NoDataMessage />
          )}
        </TableCell>
      </StyledRow>
    </>
  );
}

const StyledRow = ({
  children,
  bgColor,
}: {
  children: ReactNode;
  bgColor: string;
}) => <TableRow sx={{ backgroundColor: bgColor }}>{children}</TableRow>;

export default GroupInfo;
